import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { useStore } from 'common/store';
import { SelectValueViewer } from 'components';
import { useFundOwnershipContext } from 'context/FundOwnershipContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { FundOwnershipCell } from 'pages/CapTable/fund-ownership/types';
import { FundOwnershipTableAlias } from 'pages/CapTable/fund-ownership/utilities/rowConfig';
import { RelatedEntity } from 'pages/ValuationsAllocation/types';

const FundEntityListValueViewer = (props: { cell: FundOwnershipCell }) => {
  const { cell } = props;
  const { fundEntityListValues, setFundEntityListValues } = useFundOwnershipContext();

  const [globalStore] = useStore() as any;

  const { fundList, companyInfo } = globalStore;

  const cellValueForEntity = useMemo(
    () =>
      Object.values(cell.sheet?.cells ?? {}).find(
        c => c.alias === FundOwnershipTableAlias.ENTITY_REFERENCE && c.columnLegend === cell.columnLegend
      ) as ICell,
    [cell.sheet?.cells, cell.columnLegend]
  );

  const updateFundEntityListValues = useCallback(() => {
    if (!cellValueForEntity) {
      return;
    }

    const tmpOptions = [];

    const isPrimaryCompany = `${cellValueForEntity.value}` === `${companyInfo.id}`;

    if (Array.isArray(fundList)) {
      fundList.forEach(fund => {
        tmpOptions.push({
          id: fund.id,
          value: fund.id,
          label: fund.name,
        });
      });
    }

    if (isPrimaryCompany) {
      const options = (companyInfo.child_companies ?? []).map((child: RelatedEntity) => ({
        id: child.company_id,
        value: child.company_id,
        label: child.company_name,
      }));
      tmpOptions.push(...options);
    }

    setFundEntityListValues(tmpOptions, cell.columnLegend!);
  }, [cellValueForEntity, companyInfo, fundList, setFundEntityListValues, cell.columnLegend]);

  useEffect(() => {
    updateFundEntityListValues();
  }, [cellValueForEntity.value, updateFundEntityListValues]);

  const isChildCompany = useMemo(() => {
    if (!companyInfo?.child_companies || !cell?.value) {
      return false;
    }
    return (companyInfo.child_companies as RelatedEntity[]).some(
      child =>
        (`${child.company_id}` === `${cell.value}` || child.company_name === cell.value) &&
        `${child.company_id}` !== `${companyInfo.id}`
    );
  }, [companyInfo, cell?.value]);

  if (!isChildCompany) {
    return <SelectValueViewer {...props} options={fundEntityListValues[cell.columnLegend!]} />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <SelectValueViewer {...props} options={fundEntityListValues[cell.columnLegend!]} />
      </Box>
      <Tooltip
        title="Related Entity Allocation: Proceeds and economics flow through the related entity's allocation and cap table."
        arrow
      >
        <Chip
          label="Related Entity Allocation"
          size="small"
          color="info"
          variant="outlined"
          data-testid="related-entity-fund-allocation-badge"
          sx={{
            height: 18,
            fontSize: '0.625rem',
            fontWeight: 600,
            mx: 0.5,
            flexShrink: 0,
            cursor: 'help',
            whiteSpace: 'nowrap',
            '& .MuiChip-label': {
              px: 0.5,
              py: 0,
            },
          }}
        />
      </Tooltip>
    </Box>
  );
};

export default FundEntityListValueViewer;
